import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  MOTIF_MODAL_DATA,
  MotifButtonModule,
  MotifCardModule,
  MotifFormsModule,
  MotifMenuModule,
  MotifModalModule,
  MotifModalRef,
} from '@ey-xd/ng-motif';
import {
  CommonConstants,
  DeliverablesConstants,
} from '@sa/app/shared/constants/ApplicationConstants';
import { CreateDeliverableTemplateRequest } from '@sa/app/shared/models/create-deliverable-template-request';
import { CommonService } from '@sa/app/shared/services/common.service';
import { LoggerService } from '@sa/core/services/logger.service';
import {
  DeliverableType,
  DeliverableTypeComponent,
} from '@sa/shared/interfaces/DeliverableType';
import { WorkspaceService } from '@sa/shared/services/workspace.service';
/**
 * @component
 *
 * A standalone Angular component that displays a panel for adding deliverables.
 * It manages the visibility state using Angular signals and exposes input/output signals for parent communication.
 * The component uses Motif UI modules for consistent styling and behavior.
 */
@Component({
  selector: 'app-add-deliverable-template-panel',
  standalone: true,
  imports: [
    CommonModule,
    MotifMenuModule,
    FormsModule,
    MotifCardModule,
    MotifFormsModule,
    MotifButtonModule,
    MotifModalModule,
  ],
  templateUrl: './add-deliverable-template-panel.component.html',
  styleUrl: './add-deliverable-template-panel.component.scss',
})
export class AddDeliverableTemplatePanelComponent {
  @Output() public visibleChange: WritableSignal<boolean> = signal(false);

  protected library: any = null;
  protected processName: string = '';
  protected description: string = '';
  protected selectedDeliverableType: DeliverableType | null = null;
  protected selectedComponent: DeliverableTypeComponent | null = null;
  protected deliverableTypes: DeliverableType[] = [];
  protected AddDeliverablesHeaders =
    DeliverablesConstants.AddDeliverablesPanel.Headers;
  protected AddDeliverablesButtonTexts =
    DeliverablesConstants.AddDeliverablesPanel.ButtonTexts;
  protected AddDeliverablesTitle =
    DeliverablesConstants.DeliverablesHeaders.AddDeliverable;
  protected isComponentRequired = false;
  private readonly _specialCharPattern = /[^a-zA-Z0-9 \-]/;
  protected get isProcessNameInvalid(): boolean {
    return this._specialCharPattern.test(this.processName || '');
  }
  protected get isDescriptionInvalid(): boolean {
    return this._specialCharPattern.test(this.description || '');
  }
  private readonly _workspaceService: WorkspaceService =
    inject(WorkspaceService);
  private readonly _commonService: CommonService = inject(CommonService);
  private readonly _loggerService: LoggerService = inject(LoggerService);
  private router = inject(Router);
  private readonly modalRef = inject(MotifModalRef);
  private readonly modalData = inject(MOTIF_MODAL_DATA);

  ngOnInit() {
    this.loadDeliverableTypes();
  }

  loadDeliverableTypes(): void {
    // Assign hardcoded sample values

    this._workspaceService
      .getDeliverableTypes(
        this._commonService.getState('selectedTenantGuid') || ''
      )
      .subscribe({
        next: (types: DeliverableType[]) => {
          this.deliverableTypes = types;
        },
        error: err => {
          this._loggerService.logApiError('Failed to load deliverable types');
          console.error(err);
        },
      });
  }

  onSelectDeliverableType(selectedType: DeliverableType): void {
    this.selectedDeliverableType = selectedType;
    this.isComponentRequired =
      selectedType?.deliverableTypeCode?.toUpperCase() ===
      CommonConstants.Defaults.GL_REC;
    // Reset component selection if deliverable type changes
    this.selectedComponent = null;
  }
  onSelectComponent(selectedComp: DeliverableTypeComponent): void {
    this.selectedComponent = selectedComp;
  }
  get isCreateDisabled(): boolean {
    return (
      !this.processName ||
      this.processName.trim().length === 0 ||
      this.isProcessNameInvalid ||
      this.isDescriptionInvalid ||
      !this.selectedDeliverableType
    );
  }

  protected onCreate(): void {
    if (this.isCreateDisabled) {
      this._loggerService.logError(
        DeliverablesConstants.AddDeliverablesPanel.Message.Validate
      );
      return;
    }
    let clientId =
      this._commonService.getState('selectedTenantGuid') ||
      JSON.parse(sessionStorage.getItem('TenantId') || '""');
      const deliverable: CreateDeliverableTemplateRequest = {
      workSpaceTemplateName: this.processName.trim(),
      workSpaceTemplateDescription: this.description || '',
      deliverableTypeId:
        this.selectedDeliverableType?.deliverableTypeID ?? null,
    };
    this._workspaceService
      .createDeliverableTemplate(clientId || '', deliverable)
      .subscribe({
        next: response => {
          this._loggerService.logSuccess(
            DeliverablesConstants.AddDeliverablesPanel.Message.Success
          );
          this.modalRef.close(true);
        },
        error: err => {
          this._loggerService.logApiError(
            err?.error?.message ||
              DeliverablesConstants.AddDeliverablesPanel.Message.Error
          );
          // Close with false
          this.modalRef.close(false);
        },
      });
  }
}
